import { useExamCenter } from '@exam-center/exam-center-details';
import { FilterOperator, useSearchExamLocationRequestInfosQuery } from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { useNavigate } from '@exam-center/router';
import {
  AddIcon,
  Button,
  DescriptionOutlinedIcon,
  type GridEventListener,
  type GridRowParams,
  NoRowsOverlay,
} from '@shared/components';
import { WithPermission } from '@shared/permissions';

import {
  BaseExamLocationRequestList,
  draftColumns,
  type DraftExamLocationRequest,
} from '../../components';
import { useExamLocationRequestListSearchParams } from '../../hooks';

export default function DraftExamLocationRequestList() {
  const navigate = useNavigate();
  const { examCenter } = useExamCenter();

  const [{ locationName, page, pageSize, province }, { changePageNumber, changePageSize }] =
    useExamLocationRequestListSearchParams();

  const { data, loading } = useSearchExamLocationRequestInfosQuery({
    variables: {
      searchExamLocationRequestInfosInput: {
        paging: {
          currentPage: page,
          pageSize: pageSize,
        },
        filter: {
          searchText: {
            fields: ['nameTh'],
            value: locationName ?? '',
          },
          conditions: [
            ...(province
              ? [
                  {
                    field: 'provinceId',
                    value: province,
                    operator: FilterOperator.Equal,
                  },
                ]
              : []),
            {
              field: 'isDraft',
              value: true,
              operator: FilterOperator.Equal,
            },
            {
              field: 'examCenterCode',
              value: examCenter.code,
              operator: FilterOperator.Equal,
            },
          ],
        },
      },
    },
  });

  const examLocationRequests =
    data?.ecSearchExamLocationRequestInfos.examLocationRequestInfos ?? [];

  const totalCount = data?.ecSearchExamLocationRequestInfos.total ?? 0;

  const examLocationRequestItems: DraftExamLocationRequest[] = examLocationRequests.map(
    (examLocationRequest) => ({
      id: examLocationRequest.id,
      refCode: '-',
      buildingName: examLocationRequest.buildingName ?? examLocationRequest.building?.nameTh ?? '-',
      locationName: examLocationRequest.nameTh ?? '-',
      province: examLocationRequest.province.nameTh,
      status: 'draft',
      editedBy: examLocationRequest.editedUser
        ? `${examLocationRequest.editedUser.firstname} ${examLocationRequest.editedUser.lastname}`
        : '-',
      editedAt: examLocationRequest.editedAt,
    }),
  );

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<DraftExamLocationRequest>,
  ) => {
    navigate(`/:examCenterCode/exam-location/requests/draft/:requestId`, {
      params: {
        requestId: params.row.id,
        examCenterCode: examCenter.code,
      },
    });
  };

  const isSearchOrFiltering = (locationName?.length ?? 0) > 0 || Boolean(province);

  return (
    <BaseExamLocationRequestList
      columns={draftColumns}
      loading={loading}
      rows={examLocationRequestItems}
      sortingMode='server'
      slots={{
        noRowsOverlay: () => {
          if (isSearchOrFiltering) {
            return <NoRowsOverlay />;
          }
          return (
            <NoRowsOverlay
              title='ยังไม่มีคำขออนุมัติสถานที่สอบ'
              description='คลิกปุ่มด้านล่างเพื่อสร้างคำขออนุมัติสถานที่สอบใหม่'
              icon={DescriptionOutlinedIcon}
              action={
                <WithPermission
                  requiredPermission={[
                    APP_PERMISSIONS.EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.CREATE,
                  ]}
                >
                  <Button
                    startIcon={<AddIcon />}
                    variant='outlined'
                    onClick={() =>
                      navigate('/:examCenterCode/exam-location/requests/create', {
                        params: {
                          examCenterCode: examCenter.code,
                        },
                      })
                    }
                  >
                    สร้างคำขออนุมัติ
                  </Button>
                </WithPermission>
              }
            />
          );
        },
      }}
      slotProps={{
        footer: {
          total: totalCount,
          pagination: {
            page: page,
            count: totalCount,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onRowClick={handleRowClick}
    />
  );
}
